import { AlertInterface, AlertTypes } from "./atoms";

// Error types for voice settings error handling
type VoiceSettingsErrorType =
  "CHANGE_IN_PROGRESS" | "SERVER_ERROR" | "UNKNOWN_ERROR";

interface VoiceSettingsError {
  status?: number;
  message: string;
  type?: VoiceSettingsErrorType;
  response?: any;
  responseText?: string;
  data?: any;
}

// Error message mappings for voice settings
const VOICE_SETTINGS_ERROR_MESSAGES = {
  CHANGE_IN_PROGRESS:
    "cockpit.managementTile.voiceSettings.errors.changeInProgress",
  SERVER_ERROR: "translation:Common.generalForm.error.genericError",
} as const;

// Error detection patterns for voice settings
const VOICE_SETTINGS_ERROR_PATTERNS = {
  CHANGE_IN_PROGRESS: "change in progress",
} as const;

// Determine voice settings error type based on error message and status
const determineVoiceSettingsErrorType = (
  error: VoiceSettingsError,
): VoiceSettingsErrorType => {
  // Check if error has a specific type already set
  if (error.type) {
    return error.type;
  }

  const errorMessage = error.message.toLowerCase();

  if (error.status === 500) {
    if (
      errorMessage.includes(VOICE_SETTINGS_ERROR_PATTERNS.CHANGE_IN_PROGRESS)
    ) {
      return "CHANGE_IN_PROGRESS";
    }

    return "SERVER_ERROR";
  }

  return "UNKNOWN_ERROR";
};

// Main voice settings error handler function
export const createVoiceSettingsErrorHandler = (
  t: (key: string) => string,
  showAlert: (alert: AlertInterface) => void,
) => {
  return (error: VoiceSettingsError) => {
    // Extract the error information from the error object structure
    const errorObj: VoiceSettingsError = {
      status: error.status || 0,
      message: "",
    };

    // Try to extract the message from different possible locations
    if (error.response) {
      errorObj.status = error.response.status || error.status || 500;
      // Check if response is a string or has a text property
      if (typeof error.response === "string") {
        errorObj.message = error.response;
      } else if (error.response.data) {
        errorObj.message = error.response.data;
      }
    } else if (error.responseText) {
      errorObj.message = error.responseText;
    } else if (error.data) {
      errorObj.message = error.data;
    } else if (error.message) {
      errorObj.message = error.message;
    }

    const errorType = determineVoiceSettingsErrorType(errorObj);

    switch (errorType) {
      case "CHANGE_IN_PROGRESS":
        showAlert({
          text: t(VOICE_SETTINGS_ERROR_MESSAGES.CHANGE_IN_PROGRESS),
          type: AlertTypes.warning,
        });
        break;

      case "SERVER_ERROR":
        showAlert({
          text: t(VOICE_SETTINGS_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;

      default:
        showAlert({
          text: t(VOICE_SETTINGS_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;
    }
  };
};
